import { Box, Divider, Step, StepButton, Stepper, useMediaQuery, useTheme } from '@mui/material';
import { t } from 'i18next';
import { compact, flatten, get, groupBy, isArray, isEmpty, isNull, last, lowerCase, toNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useGallopData } from '../../hooks/useGallopData';
import { useNotificationsSettings } from '../../hooks/useNotificationSettings';
import { RepportType } from '../../pages/settings/notifications';
import { TChannel, TChannelType } from '../../shared/types/Channel.type';
import { TSchedule, scheduleType, schedules } from '../../shared/types/schedule.type';
import { Ttrack } from '../../shared/types/tracks.type';
import { TTrainerHorse } from '../../shared/types/trainerHorse.type';
import { TTrainingType } from '../../shared/types/training.type';
import { TUser } from '../../shared/types/user.type';
import { RootState } from '../../store';
import NewConfigStep1 from './NewConfigStep1';
import NewConfigStep2 from './NewConfigStep2';
import NewConfigStep3 from './NewConfigStep3';
import NewConfigStep4 from './NewConfigStep4';
import { useAuth } from '../../hooks/useAuth';
import { formatMetricIntoImperialDistance } from '../../shared/utils/conversion';

type NewConfigurationStepperProps = {
    channels: TChannel[],
    horses: TTrainerHorse[],
    tracks: Ttrack[],
    trainingTypes: TTrainingType[],
    onSaveSuccess: () => void,
    itemToEdit: TSchedule | null,
    dataLoading: boolean,
    horsesLoading: boolean,
    createNewChannel: () => void,
    deleteChannel: (id: number, type: TChannelType) => void,
    editChannel: (item: TChannel) => void,
    isLoading: boolean,
    isDeleting: boolean,
    configType?: "SIMPLE" | "ADVANCED",
    repportType: RepportType,
    onInputChange: (value: string) => void
}


function NewConfigurationStepper({ horsesLoading, repportType, configType, channels, horses, tracks, trainingTypes, onSaveSuccess, itemToEdit, dataLoading, createNewChannel, deleteChannel, editChannel, isLoading, isDeleting, onInputChange }: NewConfigurationStepperProps) {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('sm'));
    const steps = [t('configuration'), t('configuration:recipient'), t('configuration:scheduling'), t('configuration:recap')];
    const [activeStep, setActiveStep] = React.useState(0);
    const [resumePhrase, setResumePhrase] = useState('')
    const [completed, setCompleted] = React.useState<{
        [k: number]: boolean;
    }>({});
    const user = useSelector<RootState>(state => state.user?.user) as TUser
    const [selectedChannels, setSelectedChannels] = useState<string[]>([])
    const [schedules, setSchedules] = useState<Partial<schedules>[]>([])
    const [dataConfig, setDataConfig] = useState<Partial<TSchedule>>({})
    const [lastXHours, setLastXHours] = useState<number>(24); // default
    const { saveConfig, updateConfig, generatePhrase, saveStable, updateStable } = useNotificationsSettings()
    const { saveGallopReview, updateGallopReview, generateGallopPhrase } = useGallopData({ gallopDataQueryEnabled: false })
    const { auth } = useAuth()
    //config data
    const renderValueAndUnit = useCallback((v: number) => {
        let value = auth?.unit_system === 'metric' ? v : formatMetricIntoImperialDistance(v ?? 0, auth.imperial_distance_miles_only)
        let unit = auth?.unit_system === 'metric' ? t('units:meters') : null
        return compact([value, unit]).join(' ')
    }, [auth?.unit_system])

    const getDefaultName = (type: RepportType) => {
        let valueDefault = `${renderValueAndUnit(600)}<${dataConfig.max_best600}${t('units:seconds')}`
        switch (type) {
            case 'GALLOP_REPPORT':
                return t('repportType:gallop');
            case 'STABLE_REPPORT':
                return t('stable:title:report');
            case 'TRAINING_REPORT':
                return t('defaultConfiguration', {
                    value: valueDefault, interpolation: {
                        escapeValue: false
                    }
                });

            default:
                return t('defaultConfiguration', {
                    value: valueDefault, interpolation: {
                        escapeValue: false
                    }
                });
        }
    };
    //
    const [name, setName] = React.useState(() => getDefaultName(repportType));



    React.useEffect(() => {
        const newName = getDefaultName(repportType);
        setName(newName);
    }, [repportType, dataConfig.max_best600]);

    const _schedules = useMemo(() => {
        console.log({schedules,repportType});
        
        return flatten(schedules.map(e => {
            let arr: any = []
            if (isArray(e.day_of_week)) {
                e.day_of_week.forEach(i => {
                    if (['FIXED_TIME', 'LAST_X_HOURS'].includes(e.type as scheduleType)) {
                        arr.push(
                            { type: e.type, timezone: e.timezone, day_of_week: i, hour: e.hour, minute: e.minute, ...(e.type === 'LAST_X_HOURS' ? { count_hours_before: lastXHours } : {}) }
                        )
                    } else if (e.type === 'LAST_WEEK' && ['STABLE_REPPORT','TRAINING_REPORT'].includes(repportType)) {
                        arr.push(
                            { type: e.type, timezone: e.timezone, day_of_week: i, hour: e.hour, minute: e.minute }
                        )
                    } else if (e.type === 'BI_MONTHLY') {
                        arr.push(
                            { type: e.type, timezone: e.timezone, hour: e.hour, minute: e.minute }
                        )
                    } else if (e.type === 'MONTHLY') {
                        arr.push(
                            { type: e.type, timezone: e.timezone, hour: e.hour, minute: e.minute, day_of_month: e.day_of_month }
                        )
                    } else {
                        arr.push({ type: e.type, timezone: e.timezone, day_of_week: i })
                    }
                });
            }
            else {
                arr.push(e);
            }

            return arr
        }))
    }, [schedules, lastXHours, repportType])


    

    React.useEffect(() => {
        if (!isNull(itemToEdit)) {

            setName(itemToEdit.name)
            if (!isEmpty(itemToEdit.communication_channels)) {
                let ids = itemToEdit.communication_channels.map(e => get(e, '@id', ''))
                setSelectedChannels(ids)
            }

            updateSchedultes(itemToEdit.schedules)

        }
    }, [itemToEdit])




    //Fns
    const updateSchedultes = (schedules: Partial<schedules>[]) => {

        if (isEmpty(schedules)) return

        //Get Schedule Type

        let type = get(schedules, '[0].type', undefined) as scheduleType



        if (!type) return


        if (type === 'LAST_X_HOURS') {
            setLastXHours(get(schedules, '[0].count_hours_before', 24))
        }

        if (type === 'SYNCHRONISED_LAST_HOUR') {

            //we group by timezone
            let tz = groupBy(schedules, item => item?.timezone ?? "")

            Object.values(tz).forEach(i => {
                let item = i?.[0]
                if (!item) return

                let days = i.map(e => e.day_of_week) as number[]

                setSchedules(prev => ([...prev, { ...item, day_of_week: days }]))

            })

        } else {
            //We group in fixed time case by hour time and tz
            let tz = groupBy(schedules, item => String(item.hour) + String(item.minute) + String(lowerCase(item.timezone)))

            Object.values(tz).forEach(i => {
                let item = i?.[0]
                if (!item) return

                let days = i.map(e => e.day_of_week) as number[]

                setSchedules(prev => ([...prev, { ...item, day_of_week: days }]))
            })
        }

    }

    const totalSteps = () => {
        return steps.length;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && !allStepsCompleted()
                ? // It's the last step, but not all steps have been completed,
                // find the first step that has been completed
                steps.findIndex((step, i) => !(i in completed))
                : activeStep + 1;
        setActiveStep(newActiveStep);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStep = (step: number) => () => {
        setActiveStep(step);
    };
    const handleComplete = () => {
        const newCompleted = completed;
        newCompleted[activeStep] = true;
        setCompleted(newCompleted);
        handleNext();
    };
    const dto: Partial<TSchedule> = { ...dataConfig, user: user['@id'], name, communication_channels: selectedChannels, schedules: _schedules }



    useEffect(() => {
        if (activeStep === 3) {
            let promise = null

            switch (repportType) {
                case 'TRAINING_REPORT':
                    promise = generatePhrase
                    break;
                case 'GALLOP_REPPORT':
                    promise = generateGallopPhrase
                    break;
                case 'STABLE_REPPORT':
                    promise = null
                    break;
                default:
                    break;
            }
            if (!promise) return

            promise.mutateAsync(dto).then(result => {
                setResumePhrase(result.data?.preview_text ?? "")
            }).catch(() => {
                console.error('failed to fetch config recap')
            })

        }
    }, [activeStep, repportType])

    const saveConfiguration = () => {
        let promise = null
        if (repportType === 'TRAINING_REPORT') {
            promise = itemToEdit ? updateConfig.mutateAsync({ id: itemToEdit.id, config: dto }) : saveConfig.mutateAsync(dto)
        }
        if (repportType === 'GALLOP_REPPORT') {

            let gallopId = toNumber(last(itemToEdit?.['@id'].split('/')))

            promise = itemToEdit ? updateGallopReview.mutateAsync({ id: gallopId, config: dto }) : saveGallopReview.mutateAsync(dto)
        }
        if (repportType === 'STABLE_REPPORT') {

            let gallopId = toNumber(last(itemToEdit?.['@id'].split('/')))

            promise = itemToEdit ? updateStable.mutateAsync({ id: gallopId, config: dto }) : saveStable.mutateAsync(dto)
        }
        if (!promise) return

        promise
            .then(async () => {
                toast(t('rights:success'), { type: 'success' })
                setName('')
                setSelectedChannels([])
                onSaveSuccess()
            }).catch(err => {
                toast(t('error:generic'), { type: 'error' })

            })
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Divider sx={{ mb: 2 }}></Divider>
            <Stepper orientation={matches ? 'horizontal' : 'vertical'} nonLinear activeStep={activeStep}>
                {steps.map((label, index) => (
                    <Step key={label} completed={completed[index]}>
                        <StepButton color="inherit" >
                            {label}
                        </StepButton>
                    </Step>
                ))}
            </Stepper>
            <div>
                {
                    activeStep === 3 && <NewConfigStep1
                        resumePhrase={resumePhrase}
                        isPhraseLoading={generatePhrase.isLoading}
                        onBack={handleBack}
                        step={activeStep}
                        onSave={() => {
                            saveConfiguration()
                        }}
                        loading={saveConfig.isLoading || updateConfig.isLoading || updateGallopReview.isLoading || saveGallopReview.isLoading || saveStable.isLoading || updateStable.isLoading}
                        name={name} onNameChange={(value) => setName(value)}></NewConfigStep1>
                }
                {
                    activeStep === 1 && <NewConfigStep2
                        createNewChannel={createNewChannel}
                        isLoading={isLoading}
                        isDeleting={isDeleting}
                        editChannel={editChannel}
                        deleteChannel={deleteChannel}
                        onBack={handleBack} selectedChannels={selectedChannels} onChannelsChange={values => setSelectedChannels(values)} step={activeStep} onSave={() => { handleComplete() }} channels={channels}></NewConfigStep2>
                }
                {
                    activeStep === 0 && <NewConfigStep3
                        configType={configType}
                        repportType={repportType}
                        dataConfig={dataConfig}
                        dataLoading={dataLoading}
                        onInputChange={onInputChange}
                        itemToEdit={itemToEdit}
                        horsesLoading={horsesLoading}
                        onBack={handleBack} loading={saveConfig.isLoading || updateConfig.isLoading} horses={horses} tracks={tracks} trainingTypes={trainingTypes} step={activeStep} onSave={(data) => {
                            setDataConfig(data)
                            handleComplete()
                        }} ></NewConfigStep3>
                }
                {
                    activeStep === 2 && <NewConfigStep4
                        lastXHours={lastXHours}
                        setLastXHours={val => setLastXHours(val)}
                        schedules={schedules}
                        onScheduleChange={values => setSchedules(values)}
                        onBack={handleBack}
                        repportType={repportType}
                        onSave={() => {
                            handleComplete()
                        }}
                        step={activeStep}
                    ></NewConfigStep4>
                }

            </div>
        </Box>
    )
}

export default NewConfigurationStepper